import React from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'

// Usage against an allowance as a thin meter: label, "used of allowance" and a
// track that fills to the allowance. `used` and `allowance` only size the
// fill - the text shown is `usedText` / `allowanceText`, so callers format the
// figures. Over the allowance the fill turns calm amber (never red) and says
// so in words; with no allowance there is no track, only `emptyText`.
const AllowanceBar = ({
  label,
  used,
  allowance,
  usedText,
  allowanceText,
  caption,
  overText = 'Above allowance',
  emptyText = 'Not included',
  tone = '#239AA4',
  overTone = '#F28B1F',
}) => {
  const hasAllowance = typeof allowance === 'number' && Number.isFinite(allowance) && allowance > 0
  const usedNumber = Number(used) || 0
  const over = hasAllowance && usedNumber > allowance
  const fill = over ? overTone : tone
  const percent = hasAllowance ? Math.min(100, Math.max(0, (usedNumber / allowance) * 100)) : 0

  return (
    <Box minWidth={0}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={1}>
        <Typography variant="body2" fontWeight={600} color="text.primary" noWrap>
          {label}
        </Typography>
        <Typography variant="body2" color="text.primary" sx={{ whiteSpace: 'nowrap' }}>
          <Box component="span" fontWeight={700}>
            {usedText ?? usedNumber}
          </Box>{' '}
          <Box component="span" color="text.secondary">
            {hasAllowance ? `of ${allowanceText ?? allowance}` : `· ${emptyText}`}
          </Box>
        </Typography>
      </Stack>
      {hasAllowance && (
        <Box
          role="meter"
          aria-label={label}
          aria-valuemin={0}
          aria-valuemax={allowance}
          aria-valuenow={usedNumber}
          aria-valuetext={`${usedText ?? usedNumber} of ${allowanceText ?? allowance}${over ? ` - ${overText.toLowerCase()}` : ''}`}
          sx={{
            mt: 0.75,
            height: 6,
            borderRadius: 3,
            bgcolor: alpha(fill, 0.16),
            overflow: 'hidden',
          }}
        >
          <Box sx={{ width: `${percent}%`, height: '100%', borderRadius: 3, bgcolor: fill }} />
        </Box>
      )}
      {(caption || over) && (
        <Typography variant="caption" color="text.secondary" display="block" marginTop={0.5}>
          {over && (
            <Box component="span" fontWeight={700} color="#B54708">
              {overText}
              {caption ? ' · ' : ''}
            </Box>
          )}
          {caption}
        </Typography>
      )}
    </Box>
  )
}

export default AllowanceBar
